// Copies GET /api/employees/:id/eligibility onto client plans so the broker
// UI shows what the enrollment API will enforce.

export interface PlanEligibilityRow {
  planId: number;
  isEligible: boolean;
  ineligibilityReason?: string | null;
  /** First date coverage can start (waiting period + entry rule), YYYY-MM-DD. */
  effectiveDate?: string | null;
  /** Group setting, else plan's. */
  enrollmentRequired?: boolean;
}

/**
 * Returns clientPlans ([{ plan, ... }]) with each plan carrying
 * `ineligibleReason` (null when eligible), the group-aware
 * `enrollmentRequired`, and `earliestEffectiveDate`. The earliest date is only
 * set when the employee has a hire date — without one the server counts from
 * today and skips the check, so the UI shouldn't enforce it either.
 * Rows still loading (undefined) → plans returned untouched.
 */
export function withPlanEligibility(
  clientPlans: any[],
  rows: PlanEligibilityRow[] | undefined | null,
  hasHireDate: boolean,
): any[] {
  if (!rows) return clientPlans;
  const byPlanId = new Map(rows.map((r) => [r.planId, r]));
  return clientPlans.map((clientPlan) => {
    const row = byPlanId.get(clientPlan?.plan?.id);
    if (!row) return clientPlan;
    return {
      ...clientPlan,
      plan: {
        ...clientPlan.plan,
        enrollmentRequired: row.enrollmentRequired ?? clientPlan.plan.enrollmentRequired,
        ineligibleReason: row.isEligible ? null : row.ineligibilityReason || "Not eligible for this plan",
        earliestEffectiveDate: hasHireDate ? row.effectiveDate ?? null : null,
      },
    };
  });
}

export const isClientPlanEligible = (clientPlan: any) => !clientPlan?.plan?.ineligibleReason;

/**
 * Why none of these plans can be elected, or null when at least one can.
 * " - plan default" (whether the Full-Time rule came from the group or the
 * plan) matters on the plan's Eligibility tab, not on this one-line summary;
 * dropping it lets "requires Full-Time" and "requires Full-Time - plan
 * default" collapse into one reason instead of repeating.
 */
export function typeIneligibleReason(clientPlans: any[]): string | null {
  if (clientPlans.length === 0 || clientPlans.some(isClientPlanEligible)) return null;
  const reasons = clientPlans.map((cp) => String(cp.plan.ineligibleReason).replace(" - plan default", ""));
  return Array.from(new Set(reasons)).join("; ");
}
